/**
 * `useTaskTeamMembers` — resolve a tasks-team's member roster to the
 * { id, name, initials } shape the canvas needs for assignee avatars.
 *
 * Issue #759 (Phase 2). Task assignments carry only `membership_id`; the
 * display name + initials are resolved here. `GET /api/teams/{id}/members`
 * is a **generic** team-membership endpoint (returns `MemberRow[]` — id,
 * `display_name_in_team`, `abbreviation`) — it is not scheduler-specific,
 * so reading it keeps the `taskmap ⊥ scheduler` boundary clean (we depend
 * on the generated schema type + `apiFetch`, nothing from the scheduling
 * module). We deliberately do NOT reuse `useTeamMembers.ts` because that
 * file exports scheduling-flavoured *mutation* hooks; a thin taskmap-local
 * query keeps the dependency surface minimal.
 *
 * Initials: prefer the team's curated `abbreviation` (already short +
 * team-unique); else derive up to two uppercase letters from the display
 * name. Display-only — never a per-person metric (§A9).
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

type MemberRow = components["schemas"]["MemberRow"];

/** The lookup the canvas consumes. */
export interface TaskMember {
  membershipId: string;
  name: string;
  initials: string;
  /**
   * The backing account id, or null for an account-less placeholder member.
   * Used only to resolve "which of these rows is ME" (`useCurrentMember`) —
   * never displayed, never a per-person metric (§A9).
   */
  accountId: string | null;
}

const STALE_TIME_MS = 60_000;

/** Derive up-to-two-letter uppercase initials from a display name. */
export function deriveInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  if (!first) return "?";
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  const last = words[words.length - 1] as string;
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase();
}

function toTaskMember(row: MemberRow): TaskMember {
  const name = row.display_name_in_team || "Unknown";
  const initials = row.abbreviation?.trim() || deriveInitials(name);
  return { membershipId: row.id, name, initials, accountId: row.account_id ?? null };
}

/**
 * Returns a `Map<membershipId, TaskMember>` for O(1) avatar resolution,
 * plus the raw list (for member pickers). Resolves to an empty map while
 * loading or on error — avatars degrade to a neutral "?" rather than
 * throwing the canvas.
 */
export function useTaskTeamMembers(teamId: string | undefined) {
  const query = useQuery({
    queryKey: ["task-team-members", teamId ?? ""],
    enabled: Boolean(teamId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<MemberRow[]>(`/api/teams/${teamId}/members`),
  });

  // Defensive: degrade to an empty roster on anything that isn't an array
  // (a loading/errored query, or an unexpected body) — avatars fall back to
  // "?" rather than throwing the canvas.
  const rows = Array.isArray(query.data) ? query.data : [];
  const list: TaskMember[] = rows.filter((row) => row.status !== "left").map(toTaskMember);
  const byId = new Map(list.map((m) => [m.membershipId, m]));

  return { list, byId, isLoading: query.isLoading };
}
